import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAccessToken as hostGetAccessToken,
  getIdToken as hostGetIdToken,
  logout as hostLogout,
  waitForHostAuth,
} from '../services/hostAuthService';
import type { UserInfo } from '../types/user';
import { decodeJWT } from '../utils/decodeJWT';

/**
 * Null-safe wrapper around the shared `decodeJWT` util. The host id token may
 * be absent (not yet fetched) and `decodeJWT` throws on malformed input, so
 * we guard both and fall back to `null` rather than surfacing a hard error.
 * Reusing `decodeJWT` keeps a single, UTF-8-correct decode path across the
 * app instead of a bespoke reimplementation.
 */
const decodeIdToken = (token: string | null): UserInfo | null => {
  if (!token) {
    return null;
  }
  try {
    return decodeJWT(token) as UserInfo;
  } catch (err) {
    console.warn('[useHostAuth] failed to decode id token', err);
    return null;
  }
};

export interface HostAuthState {
  accessToken: string | null;
  idToken: string | null;
  idTokenData: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /**
   * Standalone PKCE has a `login()` that kicks off the redirect flow. In
   * hosted mode the host (TeamHub) has already logged the user in — there
   * is no remote-side login flow. Kept as a no-op so `UserContext`'s shape
   * stays uniform across modes.
   */
  login: () => void;
  logout: () => void;
  /**
   * Per-request token getters. Callers building an Authorization header
   * should prefer these over the cached strings so a host token refresh
   * doesn't strand us with a stale bearer.
   */
  getAccessToken: () => Promise<string | null>;
  getIdToken: () => Promise<string | null>;
}

/**
 * Token-refresh poll interval. Scuba refreshes tokens internally; we just
 * need to re-pull the new string so display/UI bits update. 5 minutes is
 * well under the typical cpaas token lifetime (~1h) and well above the
 * scuba internal refresh window.
 */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Auth hook for hosted (MF) mode. Mirrors the subset of `IAuthContext`
 * (`@sangoma/react-oauth2-code-pkce`) that `UserContext` consumes, but
 * sources every value from `window.appService` (TeamHub scuba bridge).
 *
 * Only called by the hosted branch of `UserProvider`. Never invoked in
 * standalone NG Meet.
 */
export const useHostAuth = (): HostAuthState => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [idTokenData, setIdTokenData] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const [nextAccess, nextId] = await Promise.all([hostGetAccessToken(), hostGetIdToken()]);
      if (!mountedRef.current) {
        return;
      }
      setAccessToken(nextAccess);
      setIdToken(nextId);
      setIdTokenData(decodeIdToken(nextId));
      setError(null);
    } catch (err) {
      if (!mountedRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Host auth refresh failed';
      console.warn('[useHostAuth] refresh failed', err);
      setError(message);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;

    const boot = async () => {
      const ready = await waitForHostAuth();
      if (cancelled || !mountedRef.current) {
        return;
      }
      if (!ready) {
        setError('Host auth bridge unavailable');
        setIsLoading(false);
        return;
      }
      await refresh();
      if (!mountedRef.current) {
        return;
      }
      setIsLoading(false);
      intervalId = setInterval(() => {
        void refresh();
      }, REFRESH_INTERVAL_MS);
    };

    void boot();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  const login = useCallback(() => {
    // No-op in hosted mode — the host owns the login flow.
  }, []);

  const logout = useCallback(() => {
    hostLogout();
  }, []);

  return {
    accessToken,
    idToken,
    idTokenData,
    isAuthenticated: !!idToken && !!idTokenData,
    isLoading,
    error,
    login,
    logout,
    getAccessToken: hostGetAccessToken,
    getIdToken: hostGetIdToken,
  };
};
